/**
 * Issue Comments API Service
 *
 * Service functions for interacting with the issue comments API endpoints.
 */

import { apiClient } from "./api";
import type { IssueComment, CommentType } from "@/types/issueComment";

const API_PREFIX = "/api/v1/security-issues";

/**
 * Paginated response from backend
 */
export interface PaginatedCommentsResponse {
  data: IssueComment[];
  total_count: number;
  has_more: boolean;
  page: number;
  items_per_page: number;
}

/**
 * Request body for creating a comment
 */
export interface CreateCommentData {
  content: string;
  comment_type?: CommentType;
}

/**
 * Request body for updating a comment
 */
export interface UpdateCommentData {
  content: string;
}

/**
 * List comments for a security issue.
 * @param issueId - The numeric ID of the issue
 * @param page - The page number (default: 1)
 */
export async function listIssueComments(
  issueId: number,
  page: number = 1
): Promise<PaginatedCommentsResponse> {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  return apiClient.get<PaginatedCommentsResponse>(
    `${API_PREFIX}/${issueId}/comments?${params.toString()}`
  );
}

/**
 * Create a new comment on a security issue.
 * @param issueId - The numeric ID of the issue
 * @param data - The comment data
 */
export async function createIssueComment(
  issueId: number,
  data: CreateCommentData
): Promise<IssueComment> {
  return apiClient.post<IssueComment>(`${API_PREFIX}/${issueId}/comments`, data);
}

/**
 * Update a comment on a security issue.
 * @param issueId - The numeric ID of the issue
 * @param commentId - The numeric ID of the comment
 * @param data - The update data
 */
export async function updateIssueComment(
  issueId: number,
  commentId: number,
  data: UpdateCommentData
): Promise<IssueComment> {
  return apiClient.patch<IssueComment>(
    `${API_PREFIX}/${issueId}/comments/${commentId}`,
    data
  );
}

/**
 * Delete (soft-delete) a comment on a security issue.
 * @param issueId - The numeric ID of the issue
 * @param commentId - The numeric ID of the comment
 */
export async function deleteIssueComment(
  issueId: number,
  commentId: number
): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(
    `${API_PREFIX}/${issueId}/comments/${commentId}`
  );
}
